import React from 'react';
import LocalizedStrings from 'react-localization';
import { en } from './localization/en';
import { tc } from './localization/tc';

const stringData = {
  en,
  tc,
};

const SUPPORTED_LANGUAGES = ['en', 'tc'];
const LANGUAGE_STORAGE_KEY = 'ev_composer_language';
// Unchanged from RootPage's old hard-coded setLanguage("tc"): with nothing
// stored the app still opens in Traditional Chinese. Note react-localization's
// own default would be English — a zh-TW / zh-HK browser matches neither 'en'
// nor 'tc' exactly, so it falls back to the first entry in stringData.
const DEFAULT_LANGUAGE = 'tc';

/**
 * The language the app should open in.
 *
 * Picking 繁體中文 / English in Settings (or on the login screen) used to call
 * setLanguage() and nothing else, so the choice lasted until the next reload —
 * and RootPage's constructor then forced "tc" back on every load regardless.
 * An explicit choice is now remembered and wins; otherwise the default stands.
 */
function resolveInitialLanguage() {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.indexOf(stored) >= 0) {
      return stored;
    }
  } catch (e) {
    // localStorage throws in private mode / when storage is disabled.
  }
  return DEFAULT_LANGUAGE;
}

const strings = new LocalizedStrings(stringData);
// Applied to the raw object, before the Proxy below wraps it.
strings.setLanguage(resolveInitialLanguage());

class Localization extends React.Component {
  static strings = strings;
}

const handler = {
  get(target, prop) {
    const result = Reflect.get(target, prop);
    if (typeof prop !== 'string' || prop in target || result !== undefined) {
      return result;
    }
    // Log the gap, but return undefined rather than a `[KEY]` placeholder.
    // Roughly a hundred call sites are written as
    // `Localization.strings.X || 'English fallback'`, and a truthy placeholder
    // makes every one of those render "[X]" to the user instead of the
    // fallback the author wrote.
    console.log(`[Localization] Missing string for key: ${prop}`);
    return undefined;
  }
};

Localization.strings = new Proxy(Localization.strings, handler);

/**
 * Change the interface language AND remember it across reloads. Every language
 * switcher should call this rather than Localization.strings.setLanguage(),
 * which only lasts for the current page.
 */
Localization.setLanguage = (language) => {
  if (SUPPORTED_LANGUAGES.indexOf(language) < 0) {
    return;
  }
  Localization.strings.setLanguage(language);
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch (e) {
    // Non-fatal — the language still applies for this session.
  }
};

export default Localization;
